/**
 * Cookie utility functions for session management using Cookie Store API
 */

/**
 * Get a cookie value by name
 * @param {string} name - The cookie name
 * @returns {Promise<string|null>} - The cookie value or null if not found
 */
export const getCookie = async (name) => {
  try {
    const cookie = await cookieStore.get(name);
    return cookie ? cookie.value : null;
  } catch (error) {
    console.warn('Cookie Store API not supported, falling back to document.cookie');
    // Fallback for browsers that don't support Cookie Store API
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }
};

/**
 * Set a cookie with optional expiration
 * @param {string} name - The cookie name
 * @param {string} value - The cookie value
 * @param {number} days - Number of days until expiration (default: 30)
 * @param {string} path - Cookie path (default: '/')
 */
export const setCookie = async (name, value, days = 30, path = '/') => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    await cookieStore.set({
      name,
      value,
      expires,
      path
    });
  } catch (error) {
    console.warn('Cookie Store API not supported, falling back to document.cookie');
    // Fallback for browsers that don't support Cookie Store API
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=${path}`;
  }
};

/**
 * Delete a cookie by setting it to expire in the past
 * @param {string} name - The cookie name
 * @param {string} path - Cookie path (default: '/')
 */
export const deleteCookie = async (name, path = '/') => {
  try {
    await cookieStore.delete({
      name,
      path
    });
  } catch (error) {
    console.warn('Cookie Store API not supported, falling back to document.cookie');
    // Fallback for browsers that don't support Cookie Store API
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path}`;
  }
};

/**
 * Check if a cookie exists
 * @param {string} name - The cookie name
 * @returns {Promise<boolean>} - True if cookie exists
 */
export const hasCookie = async (name) => {
  const value = await getCookie(name);
  return value !== null;
}; 